import { useState } from "react";
import { supabase } from "../supabaseClient";

export function useQuestionTeachingNotes() {
  const [selectedQuestionDetail, setSelectedQuestionDetail] = useState(null);
  const [classNotes, setClassNotes] = useState([]);
  const [loadingQuestionDetail, setLoadingQuestionDetail] = useState(false);

  const fetchQuestionDetail = async ({
    examSetId,
    questionNumber,
    examSessionId,
  }) => {
    if (!examSetId || !questionNumber) {
      alert("Missing exam set or question number.");
      return;
    }

    setLoadingQuestionDetail(true);

    const { data: question, error: questionError } = await supabase
      .from("exam_questions")
      .select(
        "id, question_number, question_text, options, correct_answer, topic, domain, difficulty, teaching_note"
      )
      .eq("exam_set_id", examSetId)
      .eq("question_number", Number(questionNumber))
      .single();

    if (questionError) {
      alert(questionError.message);
      setLoadingQuestionDetail(false);
      return;
    }

    setSelectedQuestionDetail(question);

    const { data: notes, error: notesError } = await supabase
      .from("question_teaching_notes")
      .select("id, note_text, note_type, status, created_at, updated_at")
      .eq("exam_question_id", question.id)
      .eq("exam_session_id", examSessionId)
      .eq("status", "active")
      .order("created_at", { ascending: false });

    if (notesError) {
      alert(notesError.message);
      setClassNotes([]);
    } else {
      setClassNotes(notes || []);
    }

    setLoadingQuestionDetail(false);
  };

  const saveDefaultTeachingNote = async (questionId, noteText) => {
    if (!questionId) {
      alert("Missing question id.");
      return false;
    }

    const { error } = await supabase
      .from("exam_questions")
      .update({
        teaching_note: noteText || null,
      })
      .eq("id", questionId);

    if (error) {
      alert(error.message);
      return false;
    }

    setSelectedQuestionDetail((prev) =>
      prev ? { ...prev, teaching_note: noteText || null } : prev
    );

    alert("Default teaching note saved.");
    return true;
  };

  const deleteDefaultTeachingNote = async (questionId) => {
    if (!questionId) {
      alert("Missing question id.");
      return false;
    }

    const confirmDelete = window.confirm(
      "Delete the default teaching note for this question?"
    );

    if (!confirmDelete) return false;

    const { error } = await supabase
      .from("exam_questions")
      .update({
        teaching_note: null,
      })
      .eq("id", questionId);

    if (error) {
      alert(error.message);
      return false;
    }

    setSelectedQuestionDetail((prev) =>
      prev ? { ...prev, teaching_note: null } : prev
    );

    alert("Default teaching note deleted.");
    return true;
  };

  const addClassNote = async ({ examQuestionId, examSessionId, noteText }) => {
    if (!noteText) {
      alert("Please enter a class note.");
      return false;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const { error } = await supabase.from("question_teaching_notes").insert({
      exam_question_id: examQuestionId,
      exam_session_id: examSessionId,
      note_text: noteText,
      note_type: "class_note",
      status: "active",
      created_by: session?.user?.id,
      updated_by: session?.user?.id,
    });

    if (error) {
      alert(error.message);
      return false;
    }

    alert("Class note added.");
    return true;
  };

  const updateClassNote = async (noteId, noteText) => {
    if (!noteId) {
      alert("Missing note id.");
      return false;
    }

    if (!noteText) {
      alert("Class note cannot be empty.");
      return false;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const { error } = await supabase
      .from("question_teaching_notes")
      .update({
        note_text: noteText,
        updated_by: session?.user?.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", noteId);

    if (error) {
      alert(error.message);
      return false;
    }

    alert("Class note updated.");
    return true;
  };

  const deleteClassNote = async (noteId) => {
    if (!noteId) {
      alert("Missing note id.");
      return false;
    }

    const confirmDelete = window.confirm("Delete this class note?");

    if (!confirmDelete) return false;

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const { error } = await supabase
      .from("question_teaching_notes")
      .update({
        status: "deleted",
        updated_by: session?.user?.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", noteId);

    if (error) {
      alert(error.message);
      return false;
    }

    alert("Class note deleted.");
    return true;
  };

  const closeQuestionDetail = () => {
    setSelectedQuestionDetail(null);
    setClassNotes([]);
  };

  return {
    selectedQuestionDetail,
    classNotes,
    loadingQuestionDetail,
    fetchQuestionDetail,

    saveDefaultTeachingNote,
    deleteDefaultTeachingNote,

    addClassNote,
    updateClassNote,
    deleteClassNote,

    closeQuestionDetail,
  };
}